// src/defaults/registry.ts
function deepFreeze(value) {
  if (value === null || typeof value !== "object") {
    return value;
  }
  const record = value;
  for (const key of Object.keys(record)) {
    deepFreeze(record[key]);
  }
  Object.freeze(value);
  return value;
}
var DefaultsRegistry = class {
  #store = /* @__PURE__ */ new Map();
  /**
   * Registers default values for the given document family.
   *
   * - Idempotent: registering the same family with structurally identical
   *   defaults (compared via JSON.stringify) is a no-op.
   * - Conflict: registering the same family with different defaults throws
   *   an error to prevent silent configuration drift.
   * - The provided defaults are deep-frozen before storage; the caller's
   *   original object is NOT mutated (a structural clone is frozen).
   */
  register(family, defaults) {
    if (typeof family !== "string" || family.trim().length === 0) {
      throw new TypeError("DefaultsRegistry.register: family must be a non-empty string");
    }
    if (this.#store.has(family)) {
      const existing = this.#store.get(family);
      const existingJson = JSON.stringify(existing);
      const incomingJson = JSON.stringify(defaults);
      if (existingJson === incomingJson) {
        return;
      }
      throw new Error(
        `DefaultsRegistry: conflicting defaults for family "${family}". Existing and incoming values differ. Call clear() before re-registering with different defaults.`
      );
    }
    const frozen = deepFreeze(structuredClone(defaults));
    this.#store.set(family, frozen);
  }
  /**
   * Retrieves the frozen defaults for the given family, or `undefined` if
   * the family has not been registered.
   */
  get(family) {
    return this.#store.get(family);
  }
  /**
   * Returns `true` if defaults have been registered for the given family.
   */
  has(family) {
    return this.#store.has(family);
  }
  /**
   * Returns a read-only view of all registered family → defaults entries.
   * The map itself is a new ReadonlyMap wrapper; entries are already frozen.
   */
  getAll() {
    return new Map(this.#store);
  }
  /**
   * Removes all registered defaults.
   * Primarily intended for test teardown; use with care in production code.
   */
  clear() {
    this.#store.clear();
  }
};

export {
  DefaultsRegistry
};
//# sourceMappingURL=chunk-JVII56BE.js.map